/**
 * Bridges the generic scenario fixture to the typed incident domain. The
 * fixture is validated by the shared loader; the app-specific contextPatch
 * shape (confirmation + ITASC) is applied here.
 */
import bungendoreRaw from '@data/scenarios/bungendore-smith-st.json';
import {
  parseScenario,
  createIncident,
  createUnit,
  setStage,
  setConfirmation,
  updateItasc,
  updateWaterPlan,
  toScrub,
  setClock,
  type Scenario,
  type Incident,
  type IncidentStage,
  type IncidentType,
  type IncidentLevel,
  type ConfirmationStatus,
  type DeepPartialItasc,
  type CreateUnitInput,
  type WaterPlan,
  type LngLat,
} from '@duty/shared';
import { deriveWaterPlanPatch } from './spatial';

interface ScenarioContextPatch {
  confirmation?: ConfirmationStatus;
  itasc?: DeepPartialItasc;
  resources?: CreateUnitInput[];
  waterPlan?: Partial<WaterPlan>;
}

interface RawScenarioIncident {
  name: string;
  typeReported?: IncidentType;
  location: string;
  coordinates?: LngLat;
  locality?: string;
  level?: IncidentLevel;
  leadAgency?: string;
  supportingAgencies?: string[];
}

interface RawScenario {
  incident: RawScenarioIncident;
  steps: { atElapsedSec: number; stage: IncidentStage; contextPatch?: ScenarioContextPatch }[];
}

const raw = bungendoreRaw as unknown as RawScenario;

/** Validated scenario metadata (throws if the fixture is malformed). */
export const bungendoreScenario: Scenario = parseScenario(bungendoreRaw);

function applyPatch(incident: Incident, patch?: ScenarioContextPatch): Incident {
  if (!patch) return incident;
  let next = incident;
  if (patch.confirmation) next = setConfirmation(next, patch.confirmation);
  if (patch.itasc) next = updateItasc(next, patch.itasc);
  // Resources are the full set at this step (replace, so cumulative rebuilds
  // don't duplicate units across steps).
  if (patch.resources) {
    next = { ...next, resources: patch.resources.map((u) => createUnit(u)) };
  }
  if (patch.waterPlan) next = updateWaterPlan(next, patch.waterPlan);
  return next;
}

export const SCENARIO_INCIDENT_ID = `inc_${bungendoreScenario.id}`;

/**
 * Build the scripted incident state as of a given step index, applying every
 * step up to and including it cumulatively. Rebuilding from the seed keeps the
 * timeline coherent when scrubbing to any stage (forward or back).
 */
function buildAtStepIndex(targetIdx: number, identity?: { id: string; startedAt: string }): Incident {
  const seed = raw.incident;
  let incident = createIncident({
    id: identity?.id ?? SCENARIO_INCIDENT_ID,
    name: seed.name,
    typeReported: seed.typeReported,
    location: seed.location,
    coordinates: seed.coordinates,
    locality: seed.locality,
    level: seed.level,
    leadAgency: seed.leadAgency,
    supportingAgencies: seed.supportingAgencies,
    stage: raw.steps[0]?.stage ?? 'Dispatch',
    startedAt: identity?.startedAt ?? new Date().toISOString(),
  });
  for (let i = 0; i <= targetIdx && i < raw.steps.length; i++) {
    const step = raw.steps[i]!;
    incident = applyPatch(incident, step.contextPatch);
    incident = setStage(incident, step.stage);
  }
  // Derive the Water Plan (nearest hydrant / static water) from seeded spatial
  // data — computed, not echoed.
  const waterPatch = deriveWaterPlanPatch(incident);
  if (waterPatch) incident = updateWaterPlan(incident, waterPatch);

  const target = raw.steps[Math.min(targetIdx, raw.steps.length - 1)]!;
  incident = setClock(incident, toScrub(incident.clock, target.atElapsedSec));
  return incident;
}

/** Build the incident at T0 (first scripted step). */
export function buildScenarioIncident(): Incident {
  return buildAtStepIndex(0);
}

/**
 * Scrub the scripted scenario to a stage: re-applies the timeline up to that
 * stage (cumulative) and pins the scrub clock to its scripted elapsed time, so
 * the ITASC and clock reflect the whole story to that point — not just the last
 * step. Preserves the incident's identity so persistence stays stable.
 */
export function advanceToStage(incident: Incident, stage: IncidentStage): Incident {
  const idx = raw.steps.findIndex((s) => s.stage === stage);
  if (idx < 0) return setStage(incident, stage);
  return buildAtStepIndex(idx, { id: incident.id, startedAt: incident.createdAt });
}
